import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Store as StoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Wallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { fetchInactiveBranches, deleteBranch, fetchBranchCities } from '../branchesApi';
import { fetchBranchFilters } from '../api/branchFiltersApi';
import { useTranslation } from 'react-i18next';

const AcceptingStores = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilters, setBranchFilters] = useState({});
  // ✅ NEW: Delivery cities state
  const [branchCities, setBranchCities] = useState({});

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    setLoading(true);
    try {
      const data = await fetchInactiveBranches();
      console.log('📦 Inactive branches loaded:', data);
      
      setBranches(data || []);
      
      const filtersMap = {};
      const citiesMap = {};

      await Promise.all((data || []).map(async (branch) => {
        const branchId = branch.Id || branch.id;
        try {
          const filters = await fetchBranchFilters(branchId);
          filtersMap[branchId] = filters;
        } catch (err) {
          console.error('Error fetching filters for branch:', branch, err);
        }
        // ✅ NEW: Fetch cities for each branch
        try {
          const cities = await fetchBranchCities(branchId);
          citiesMap[branchId] = cities;
        } catch (err) {
          console.error('Error fetching cities for branch:', branchId, err);
        }
      }));

      setBranchFilters(filtersMap);
      setBranchCities(citiesMap);
    } catch (error) {
      console.error('❌ Error loading stores:', error);
      Swal.fire(t('Error') || 'Error', t('BranchLoadFail') || 'Failed to load stores', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('Are_You_Sure') || 'Are you sure?',
      text: t('DeleteWarning') || 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Delete_It') || 'Yes, delete it!',
      cancelButtonText: t('Cancel') || 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBranch(id);
          Swal.fire(
            t('Deleted') || 'Deleted!', 
            t('BranchDeletedSuccess') || 'Branch has been deleted.', 
            'success'
          );
          loadBranches();
        } catch (error) {
          Swal.fire(
            t('Error') || 'Error!', 
            t('BranchDeleteFail') || 'Failed to delete branch.', 
            'error'
          );
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={t('AcceptingStores') || 'Accepting Stores'}
          subheader={t('AcceptingStoresDesc') || 'Manage pending store registrations awaiting activation'}
          avatar={<StoreIcon color="warning" fontSize="large" />}
          action={
            <Chip 
              label={`${branches.length} ${t('PendingStores') || 'Pending'}`}
              color="warning"
              size="large"
              sx={{ fontWeight: 700, px: 2 }}
            />
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('ID') || 'ID'}</TableCell>
                    <TableCell>{t('Image') || 'Image'}</TableCell>
                    <TableCell>{t('Name') || 'Name'}</TableCell>
                    <TableCell>{t('Phone') || 'Phone'}</TableCell>
                    <TableCell>{t('Address') || 'Address'}</TableCell>
                    <TableCell>{t('Status') || 'Status'}</TableCell>
                    <TableCell>{t('DeliveryFee') || 'Delivery Fee'}</TableCell>
                    {/* ✅ NEW: Delivery Cities column header */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalShippingIcon fontSize="small" />
                        {t('DeliveryCities') || 'Delivery Cities'}
                      </Box>
                    </TableCell>
                    <TableCell>{t('Description') || 'Description'}</TableCell>
                    <TableCell>{t('Filters') || 'Filters'}</TableCell>
                    <TableCell align="right">{t('Actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branches.map((branch) => {
                    const branchId = branch.Id || branch.id;
                    const branchName = branch.Name || branch.name;
                    const branchPhone = branch.PhoneNumber || branch.phoneNumber;
                    const branchAddress = branch.Address || branch.address;
                    const branchDescription = branch.Description || branch.description;
                    const branchImage = branch.ImageUrl || branch.imageUrl || branch.Image || branch.image;
                    const deliveryFeeType = branch.deliveryFeeType || branch.deliveryfeetype;
                    const deliveryFeeValue = branch.deliveryFeeValue || branch.deliveryfeevalue || 0;
                    // ✅ NEW: Get cities for this branch
                    const cities = branchCities[branchId] || [];

                    return (
                      <TableRow 
                        key={branchId} 
                        sx={{ 
                          bgcolor: 'rgba(255, 152, 0, 0.08)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 152, 0, 0.15)'
                          }
                        }}
                      >
                        <TableCell><strong>{branchId}</strong></TableCell>
                        <TableCell>
                          {branchImage ? (
                            <img 
                              src={branchImage} 
                              alt="Store" 
                              style={{ 
                                width: 60, 
                                height: 40, 
                                objectFit: 'cover', 
                                borderRadius: 4, 
                                border: '2px solid #ff9800' 
                              }} 
                            />
                          ) : (
                            <Box sx={{ 
                              width: 60, 
                              height: 40, 
                              bgcolor: 'grey.200', 
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <StoreIcon sx={{ color: 'grey.400', fontSize: 24 }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {branchName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{branchPhone || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                            {branchAddress || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={t('Inactive') || 'Pending'} 
                            color="warning" 
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          {deliveryFeeType === 'byKm'
                            ? `${deliveryFeeValue} ${t('PerKm') || '/km'}`
                            : `${deliveryFeeValue} ${t('Currency') || 'YER'}`}
                        </TableCell>
                        {/* ✅ NEW: Delivery Cities cell */}
                        <TableCell>
                          {cities.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {cities.length <= 3 ? (
                                cities.map((city) => (
                                  <Chip
                                    key={city.Id || city.id}
                                    label={city.Name || city.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))
                              ) : (
                                <>
                                  {cities.slice(0, 2).map((city) => (
                                    <Chip
                                      key={city.Id || city.id}
                                      label={city.Name || city.name}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                  <Tooltip
                                    title={cities.slice(2).map(c => c.Name || c.name).join(', ')}
                                    arrow
                                  >
                                    <Chip
                                      label={`+${cities.length - 2}`}
                                      size="small"
                                      color="default"
                                      variant="filled"
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="warning.main">
                              {t('NoCitiesSelected') || 'No cities'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                            {branchDescription || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150 }}>
                            {(branchFilters[branchId] || []).map(f => f.name).join(', ') || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            color="info" 
                            size="small"
                            onClick={() => navigate(`/admin/wallets/branch/${branchId}`)}
                            title={t('ManageWallets') || 'Manage Wallets'}
                          >
                            <WalletIcon />
                          </IconButton>
                          
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => navigate(`/admin/branches/edit/${branchId}`)}
                            title={t('Edit') || 'Edit'}
                          >
                            <EditIcon />
                          </IconButton>
                          
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={() => handleDelete(branchId)}
                            title={t('Delete') || 'Delete'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {branches.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Box sx={{ py: 4 }}>
                          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            {t('NoStoresToAccept') || 'No pending stores to accept'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('AllStoresAccepted') || 'All stores have been reviewed'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcceptingStores;